// ==================== CONTACT GROWTH COMPONENT ====================

import { motion } from 'framer-motion';
import { Users, TrendingUp, TrendingDown, UserPlus, UserMinus } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { cn } from '../../../utils/helpers';
import { staggerItem } from '../../../utils/animations';
import type { ContactGrowthData } from '../../../services/customer-dashboard.service';

// ==================== TYPES ====================

interface ContactGrowthProps {
  data: ContactGrowthData | null;
  isLoading?: boolean;
}

// ==================== GROWTH CHART ====================

const GrowthChart = ({ data }: { data: Array<{ date: string; total: number; new: number }> }) => {
  if (!data || data.length === 0) return null;
  
  // Use "new" contacts for bar heights (more meaningful for growth visualization)
  const maxNew = Math.max(...data.map(d => d.new), 1);
  
  return (
    <div className="flex flex-col">
      {/* Chart bars area */}
      <div className="flex items-end gap-2 h-12">
        {data.slice(-14).map((day) => {
          const heightPercent = Math.max((day.new / maxNew) * 100, 20);
          return (
            <div key={day.date} className="flex-1 min-w-[24px] max-w-[50px] h-full flex items-end group relative">
              {/* Tooltip */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block z-10">
                <div className="bg-(--color-midnight-navy) border border-(--color-border-gray) rounded px-2 py-1 text-[10px] whitespace-nowrap shadow-lg">
                  <p className="text-(--color-muted-gray)">{new Date(day.date).toLocaleDateString()}</p>
                  <p className="text-white">Total: {day.total.toLocaleString()}</p>
                  <p className="text-green-400">+{day.new} new</p>
                </div>
              </div>
              
              {/* Bar */}
              <div 
                className="w-full bg-gradient-to-t from-(--color-signal-orange) to-(--color-signal-orange)/60 rounded-t cursor-pointer transition-all duration-300 hover:from-(--color-signal-orange) hover:to-(--color-signal-orange)/80"
                style={{ height: `${heightPercent}%` }}
              />
            </div>
          );
        })}
      </div>
      
      {/* Date labels */}
      <div className="flex gap-2 mt-1">
        {data.slice(-14).map((day) => (
          <p key={`label-${day.date}`} className="flex-1 min-w-[24px] max-w-[50px] text-[8px] text-(--color-muted-gray) text-center truncate">
            {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </p>
        ))}
      </div>
    </div>
  );
};

// ==================== COMPONENT ====================

export const ContactGrowth = ({ data, isLoading = false }: ContactGrowthProps) => {
  if (isLoading) {
    return (
      <motion.div variants={staggerItem}>
        <Card>
          <CardHeader className="border-b-0 pb-1 mb-0">
            <CardTitle className="text-base">Contact Growth</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="space-y-2 animate-pulse">
              <div className="grid grid-cols-3 gap-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-14 bg-(--color-border-gray) rounded" />
                ))}
              </div>
              <div className="h-16 bg-(--color-border-gray) rounded" />
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  const growthIsPositive = (data?.totals.growthRate || 0) > 0;

  return (
    <motion.div variants={staggerItem}>
      <Card>
        <CardHeader className="border-b-0 pb-1 mb-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-1.5">
              <Users size={14} className="text-(--color-signal-orange)" />
              Contact Growth
            </CardTitle>
            {data && (
              <div className={cn(
                'flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full',
                growthIsPositive 
                  ? 'bg-green-500/10 text-green-400' 
                  : data.totals.growthRate < 0 
                    ? 'bg-red-500/10 text-red-400' 
                    : 'bg-(--color-muted-gray)/10 text-(--color-muted-gray)'
              )}>
                {growthIsPositive ? (
                  <TrendingUp size={12} />
                ) : data.totals.growthRate < 0 ? (
                  <TrendingDown size={12} />
                ) : null}
                {data.totals.growthRate > 0 ? '+' : ''}{data.totals.growthRate}%
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          <div className="space-y-3">
            {/* Stats Grid */}
            <div className="grid grid-cols-3 gap-2">
              <div className="text-center p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
                <p className="text-lg font-bold text-white">
                  {(data?.totals.totalContacts || 0).toLocaleString()}
                </p>
                <p className="text-[10px] text-(--color-muted-gray)">Total</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
                <div className="flex items-center justify-center gap-1">
                  <UserPlus size={12} className="text-green-400" />
                  <p className="text-lg font-bold text-green-400">
                    {(data?.totals.newContacts || 0).toLocaleString()}
                  </p>
                </div>
                <p className="text-[10px] text-(--color-muted-gray)">New</p>
              </div>
              <div className="text-center p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
                <div className="flex items-center justify-center gap-1">
                  <UserMinus size={12} className="text-red-400" />
                  <p className="text-lg font-bold text-red-400">
                    {(data?.totals.optedOutContacts || 0).toLocaleString()}
                  </p>
                </div>
                <p className="text-[10px] text-(--color-muted-gray)">Opted Out</p>
              </div>
            </div>

            {/* Growth Chart */}
            <div className="p-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
              <p className="text-[10px] text-(--color-muted-gray) mb-1">New Contacts by Day</p>
              {data?.dailyData && data.dailyData.length > 0 ? (
                <div className="flex justify-center">
                  <GrowthChart data={data.dailyData} />
                </div>
              ) : (
                <div className="h-16 flex items-center justify-center text-xs text-(--color-muted-gray)">
                  No new contacts in selected period
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default ContactGrowth;
